import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Upload, ArrowLeft, Trash2 } from "lucide-react";

export default function ProductFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "", design_code: "", sku: "", description: "", product_category_id: "",
    sub_category: "", labels: "",
    metal: "gold", purity: "22K", gross_weight: "", net_weight: "", weight_grams: "", stone_details: "",
    making_charge: 0, pricing_mode: "gold-rate-based", base_price: 0, is_price_visible: true,
    tags: "", status: "in-stock", featured: false, media: [],
  });

  useEffect(() => { (async () => {
    const { data: cats } = await api.get("/product-categories");
    setCategories(cats);
    if (isEdit) {
      const { data: p } = await api.get(`/products/${id}`);
      setForm({
        title: p.title || "", design_code: p.design_code || "", sku: p.sku || "", description: p.description || "",
        product_category_id: p.product_category_id || "",
        sub_category: p.sub_category || "",
        labels: (p.labels || []).join(", "),
        metal: p.metal || "gold", purity: p.purity || "",
        gross_weight: p.gross_weight ?? "", net_weight: p.net_weight ?? "",
        weight_grams: p.weight_grams ?? "",
        stone_details: p.stone_details || "", making_charge: p.making_charge ?? 0,
        pricing_mode: p.pricing_mode || "gold-rate-based", base_price: p.base_price ?? 0,
        is_price_visible: !!p.is_price_visible,
        tags: (p.tags || []).join(", "), status: p.status || "in-stock", featured: !!p.featured,
        media: p.media || [],
      });
    }
  })(); }, [id, isEdit]);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, media: [...f.media, { url: data.url, thumb_url: data.thumb_url, kind: "image" }] }));
    } catch { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const payload = {
      ...form,
      design_code: form.design_code || null,
      sub_category: form.sub_category || null,
      labels: form.labels.split(",").map(x => x.trim()).filter(Boolean),
      gross_weight: form.gross_weight === "" ? null : Number(form.gross_weight),
      net_weight: form.net_weight === "" ? null : Number(form.net_weight),
      weight_grams: form.weight_grams === "" ? null : Number(form.weight_grams),
      making_charge: Number(form.making_charge) || 0,
      base_price: Number(form.base_price) || 0,
      tags: form.tags.split(",").map(x => x.trim()).filter(Boolean),
    };
    try {
      if (isEdit) {
        await api.patch(`/products/${id}`, payload);
        toast.success("Product updated");
      } else {
        const { data } = await api.post("/products", payload);
        toast.success("Product created");
        nav(`/products/${data.id}`); return;
      }
      nav(`/products/${id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="product-form-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Catalog</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">{isEdit ? "Edit Product" : "New Product"}</h1>
      </div>

      <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Title*</Label><Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} className="mt-1" data-testid="product-title-input"/></div>
          <div><Label>Design Code <span className="text-[#0F5E39]">(primary identifier)</span></Label><Input value={form.design_code} onChange={(e)=>setForm({...form, design_code:e.target.value})} placeholder="TARU-RING-001" className="mt-1" data-testid="product-design-code"/></div>
          <div><Label>SKU (optional)</Label><Input value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})} className="mt-1"/></div>
          <div><Label>Sub-category</Label><Input value={form.sub_category} onChange={(e)=>setForm({...form, sub_category:e.target.value})} placeholder="Kada, Chain, Jhumka…" className="mt-1" data-testid="product-subcategory"/></div>
        </div>
        <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="mt-1"/></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>Category</Label>
            <Select value={form.product_category_id || undefined} onValueChange={(v)=>setForm({...form, product_category_id:v})}>
              <SelectTrigger className="mt-1" data-testid="product-category-select"><SelectValue placeholder="Select"/></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Metal</Label>
            <Select value={form.metal} onValueChange={(v)=>setForm({...form, metal:v})}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{["gold","silver","platinum","diamond","other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Purity</Label><Input value={form.purity} onChange={(e)=>setForm({...form, purity:e.target.value})} placeholder="22K / 950 / 92.5" className="mt-1"/></div>
          <div><Label>Weight (grams) <span className="text-[#0F5E39]">(hero)</span></Label><Input type="number" step="0.01" value={form.weight_grams} onChange={(e)=>setForm({...form, weight_grams:e.target.value})} className="mt-1" data-testid="product-weight-grams"/></div>
          <div><Label>Gross weight (g)</Label><Input type="number" step="0.01" value={form.gross_weight} onChange={(e)=>setForm({...form, gross_weight:e.target.value})} className="mt-1"/></div>
          <div><Label>Net weight (g)</Label><Input type="number" step="0.01" value={form.net_weight} onChange={(e)=>setForm({...form, net_weight:e.target.value})} className="mt-1"/></div>
          <div><Label>Making charge (₹)</Label><Input type="number" value={form.making_charge} onChange={(e)=>setForm({...form, making_charge:e.target.value})} className="mt-1"/></div>
        </div>
        <div><Label>Labels (comma-separated) <span className="text-[#4A5A50] text-xs">e.g. New, Bestseller, Bridal</span></Label><Input value={form.labels} onChange={(e)=>setForm({...form, labels:e.target.value})} className="mt-1" data-testid="product-labels"/></div>
        <div><Label>Stone details</Label><Input value={form.stone_details} onChange={(e)=>setForm({...form, stone_details:e.target.value})} className="mt-1"/></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>Pricing mode</Label>
            <Select value={form.pricing_mode} onValueChange={(v)=>setForm({...form, pricing_mode:v})}>
              <SelectTrigger className="mt-1" data-testid="product-pricing-mode"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="fixed">Fixed price</SelectItem><SelectItem value="gold-rate-based">Gold-rate based</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Base price (₹)</Label><Input type="number" value={form.base_price} onChange={(e)=>setForm({...form, base_price:e.target.value})} className="mt-1" disabled={form.pricing_mode === "gold-rate-based"}/></div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v)=>setForm({...form, status:v})}>
              <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{["in-stock","made-to-order","sold","hidden"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={(e)=>setForm({...form, tags:e.target.value})} placeholder="bridal, temple, minimal" className="mt-1"/></div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2"><Switch checked={form.is_price_visible} onCheckedChange={(v)=>setForm({...form, is_price_visible:v})} data-testid="product-price-visible"/> <span className="text-sm">Show price publicly</span></label>
          <label className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v)=>setForm({...form, featured:v})} data-testid="product-featured"/> <span className="text-sm">Featured</span></label>
        </div>

        <div>
          <Label>Product media</Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {form.media.map((m, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-[#E4DFCF]">
                <img src={fileUrl(m.thumb_url || m.url)} alt="" className="w-full h-full object-cover"/>
                <button type="button" onClick={() => setForm(f => ({ ...f, media: f.media.filter((_,x)=>x!==i) }))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12}/></button>
              </div>
            ))}
            <label className="w-24 h-24 border-2 border-dashed border-[#E4DFCF] rounded-lg flex items-center justify-center cursor-pointer hover:border-[#C9A24B] text-[#4A5A50]" data-testid="upload-product-image">
              <Upload size={16}/>
              <input type="file" accept="image/*" className="hidden" onChange={(e)=>e.target.files?.[0] && upload(e.target.files[0])}/>
            </label>
          </div>
        </div>

        <Button onClick={submit} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="save-product-btn">{isEdit ? "Save changes" : "Create Product"}</Button>
      </CardContent></Card>
    </div>
  );
}
